// Drives the /presentations/new screen. The form→uploading-card
// flip is one-way: a tus failure mid-upload leaves an orphan draft
// row that the user has to delete from the presentations list
// before retrying.

import {
  tusUpload,
  formatBytes,
  throttleByIntegerPercent,
} from "./presentation_tus.js";
import { onceGuard } from "./bind_once.js";
import { postForm } from "./presigned_upload.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-presentation-create]";

export function initPresentationCreate(root = document) {
  const widgets = root.querySelectorAll
    ? root.querySelectorAll(ROOT_SELECTOR)
    : [];
  widgets.forEach(wireWidget);
}

function wireWidget(el) {
  if (!firstBind(el)) return;

  const form = el.querySelector("[data-create-form]");
  const formWrapper = el.querySelector("[data-create-form-wrapper]");
  const fileInput = el.querySelector("[data-create-file-input]");
  const dropzone = el.querySelector("[data-create-file-dropzone]");
  const fileEmpty = el.querySelector("[data-create-file-empty]");
  const fileSelected = el.querySelector("[data-create-file-selected]");
  const fileNameEl = el.querySelector("[data-create-file-name]");
  const fileSizeEl = el.querySelector("[data-create-file-size]");
  const formErrorEl = el.querySelector("[data-create-error]");
  const submitBtn = el.querySelector("[data-create-submit]");

  const uploadingCard = el.querySelector("[data-create-uploading]");
  const uploadingTitleEl = el.querySelector("[data-create-uploading-title]");
  const uploadingErrorEl = el.querySelector("[data-create-uploading-error]");
  const progressBar = el.querySelector("[data-create-progress-bar]");
  const progressMessage = el.querySelector("[data-create-progress-message]");
  const progressPercent = el.querySelector("[data-create-progress-percent]");
  const backLink = el.querySelector("[data-create-back-link]");

  if (!form || !fileInput) return;

  fileInput.addEventListener("change", () => {
    const file = fileInput.files && fileInput.files[0];
    if (!file) {
      fileEmpty.style.opacity = "1";
      fileSelected.style.opacity = "0";
      return;
    }

    fileEmpty.style.opacity = "0";
    fileSelected.style.opacity = "1";
    if (fileNameEl) fileNameEl.textContent = file.name;
    if (fileSizeEl) fileSizeEl.textContent = formatBytes(file.size);
  });

  if (dropzone) {
    dropzone.addEventListener("dragenter", () => {
      dropzone.dataset.dragging = "true";
    });
    dropzone.addEventListener("dragover", (event) => {
      event.preventDefault();
    });
    dropzone.addEventListener("dragleave", (event) => {
      if (!dropzone.contains(event.relatedTarget)) {
        dropzone.dataset.dragging = "false";
      }
    });
    dropzone.addEventListener("drop", (event) => {
      event.preventDefault();
      dropzone.dataset.dragging = "false";
      const dt = event.dataTransfer;
      if (!dt || !dt.files || dt.files.length === 0) return;
      fileInput.files = dt.files;
      fileInput.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  form.addEventListener("submit", async (event) => {
    event.preventDefault();

    const file = fileInput.files && fileInput.files[0];
    if (!file) {
      showError(formErrorEl, "Choose a video file before submitting.");
      return;
    }

    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (key === "video") return;
      if (value instanceof File) return;
      params.append(key, value);
    });
    params.append("file_size_bytes", String(file.size));

    const title = (params.get("title") || "").trim();

    setSubmitting(submitBtn, true);
    hideError(formErrorEl);

    let payload;
    try {
      payload = await postForm("/presentations", params);
    } catch (err) {
      console.error("presentations.create: metadata POST failed", err);
      showError(
        formErrorEl,
        err.userMessage ||
          "Could not create the presentation. Please try again.",
      );
      setSubmitting(submitBtn, false);
      return;
    }

    if (!payload || !payload.upload_url || !payload.presentation_id) {
      showError(formErrorEl, "Server response was missing upload details.");
      setSubmitting(submitBtn, false);
      return;
    }

    // Flip from form view → uploading card view: fade the form
    // wrapper out, hide it after the transition, then mount the
    // card. animate-rise on the card handles the entrance.
    if (uploadingTitleEl) uploadingTitleEl.textContent = title;
    if (formWrapper) {
      formWrapper.classList.add("opacity-0");
      setTimeout(() => formWrapper.classList.add("hidden"), 150);
    }
    setTimeout(() => {
      if (uploadingCard) uploadingCard.classList.remove("hidden");
    }, 150);

    if (backLink) backLink.classList.add("hidden");
    const beforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    const clearGuards = () => {
      window.removeEventListener("beforeunload", beforeUnload);
      if (backLink) backLink.classList.remove("hidden");
    };

    runUpload({
      file,
      uploadURL: payload.upload_url,
      presentationID: payload.presentation_id,
      progressBar,
      progressMessage,
      progressPercent,
      uploadingErrorEl,
      clearGuards,
    });
  });
}

function runUpload({
  file,
  uploadURL,
  presentationID,
  progressBar,
  progressMessage,
  progressPercent,
  uploadingErrorEl,
  clearGuards,
}) {
  setProgress(progressBar, progressPercent, progressMessage, 0, file.size, 0);

  tusUpload({
    file,
    uploadURL,
    onError: (err) => {
      console.error("presentations.create: tus upload failed", err);
      clearGuards();
      if (progressMessage) progressMessage.textContent = "Upload paused.";
      showError(
        uploadingErrorEl,
        "Upload failed. Delete this draft from your presentations list, then try again with a fresh upload.",
      );
    },
    onProgress: throttleByIntegerPercent((uploaded, total, pct) => {
      setProgress(
        progressBar,
        progressPercent,
        progressMessage,
        uploaded,
        total,
        pct,
      );
    }),
    onSuccess: () => {
      setProgress(
        progressBar,
        progressPercent,
        progressMessage,
        file.size,
        file.size,
        100,
      );
      if (progressBar) {
        progressBar.classList.remove("bg-brand-500");
        progressBar.classList.add("bg-emerald-500");
      }
      if (progressMessage) {
        progressMessage.textContent =
          "Upload complete. Your video is being processed…";
      }
      clearGuards();
      window.location.assign(`/presentations/${presentationID}`);
    },
  });
}

function setProgress(bar, percentEl, messageEl, uploaded, total, pct) {
  if (bar) bar.style.width = `${pct}%`;
  if (percentEl) percentEl.textContent = `${pct}%`;
  if (messageEl) {
    messageEl.textContent = `Uploading ${formatBytes(uploaded)} of ${formatBytes(total)}`;
  }
}

function showError(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}

function hideError(el) {
  if (!el) return;
  el.classList.add("hidden");
  el.textContent = "";
}

function setSubmitting(btn, isSubmitting) {
  if (!btn) return;
  btn.disabled = isSubmitting;
  btn.classList.toggle("opacity-60", isSubmitting);
  btn.classList.toggle("cursor-not-allowed", isSubmitting);
}
